import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Formik } from "formik";
import * as Yup from "yup";

import { addProduct } from "src/api/inventory";
import { NumberField, SelectField, TextAreaField, TextField } from "src/components/form";
import { Button } from "src/components/ui/button";
import { usePortalContext } from "src/context/PortalContext";
import { useDashboardContext } from "src/context/DashboardContext";
import { showSuccessToast } from "src/lib/utils";
import type { ProductRow } from "src/types/inventory";

const validationSchema = Yup.object().shape({
	name: Yup.string().required("This field is required"),
	sku: Yup.string().required("This field is required"),
	cost_price: Yup.string().required("This field is required"),
	price: Yup.string().required("This field is required"),
	qty: Yup.number().required("This field is required"),
	store_or_warehouse_id: Yup.string().required("This field is required"),
	description: Yup.string(),
});

interface ProductFormValues {
	name: string;
	sku: string;
	price: string;
	qty: number | string;
	cost_price: string;
	description: string;
	store_or_warehouse_id: string;
	image: File | null;
}

const initialValues: ProductFormValues = {
	name: "",
	sku: "",
	price: "",
	qty: 0,
	cost_price: "",
	description: "",
	store_or_warehouse_id: "",
	image: null,
};

interface AddProductModalProps {
	/** Fired with the created product — lets a caller select it inline (e.g. the invoice form). */
	onSubmit?: (product?: ProductRow) => void;
}

const AddProductModal = ({ onSubmit }: AddProductModalProps) => {
	const closePortal = usePortalContext();
	const queryClient = useQueryClient();
	const { entities } = useDashboardContext();

	const entityOptions = entities.map(({ id, name }) => ({ label: name, value: String(id) }));

	const { mutate, isLoading } = useMutation(addProduct, {
		onSuccess: ({ data }) => {
			showSuccessToast("Product added successfully");
			queryClient.invalidateQueries(["inventory"]);
			closePortal();
			onSubmit?.(data);
		},
	});

	return (
		<Formik initialValues={initialValues} validationSchema={validationSchema} onSubmit={(values) => mutate(values)}>
			{({ values, handleChange, handleSubmit, setFieldValue, errors, touched }) => (
				<form onSubmit={handleSubmit} className="tw-grid tw-gap-4">
					<div className="tw-grid tw-gap-4 sm:tw-grid-cols-2">
						<TextField
							label="Name"
							name="name"
							placeholder="Product name"
							value={values.name}
							error={touched.name && errors.name}
							onChange={handleChange}
							required
						/>
						<TextField
							label="SKU"
							name="sku"
							placeholder="SKU"
							value={values.sku}
							error={touched.sku && errors.sku}
							onChange={handleChange}
							required
						/>
						<NumberField
							label="Quantity"
							name="qty"
							placeholder="0"
							value={values.qty}
							error={touched.qty && errors.qty}
							onChange={(value) => setFieldValue("qty", value)}
							required
						/>
						<SelectField
							label="Store / warehouse"
							name="store_or_warehouse_id"
							placeholder="Select store or warehouse"
							value={values.store_or_warehouse_id}
							error={touched.store_or_warehouse_id && errors.store_or_warehouse_id}
							onChange={(value) => setFieldValue("store_or_warehouse_id", value)}
							options={entityOptions}
							emptyMessage="No stores or warehouses yet"
							required
						/>
						<NumberField
							label="Cost price"
							name="cost_price"
							placeholder="0.00"
							value={values.cost_price}
							error={touched.cost_price && errors.cost_price}
							onChange={(value) => setFieldValue("cost_price", value)}
							required
						/>
						<NumberField
							label="Selling price"
							name="price"
							placeholder="0.00"
							value={values.price}
							error={touched.price && errors.price}
							onChange={(value) => setFieldValue("price", value)}
							required
						/>
					</div>

					<TextAreaField
						label="Description"
						name="description"
						placeholder="What is this product?"
						value={values.description}
						error={touched.description && errors.description}
						onChange={handleChange}
					/>

					<TextField
						label="Image"
						name="image"
						type="file"
						accept="image/*"
						hint="Optional"
						onChange={(event) => setFieldValue("image", event.currentTarget.files?.[0] ?? null)}
					/>

					<Button type="submit" variant="secondary" className="tw-mt-1 tw-w-full" disabled={isLoading}>
						{isLoading ? "Submitting..." : "Create product"}
					</Button>
				</form>
			)}
		</Formik>
	);
};

export default AddProductModal;
